import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { SearchBar } from "#/components/SearchBar";
import { ASSETS } from "#/lib/assets";
import {
  type SaleSearch,
  type SetSaleSearch,
  saleSearchSchema,
} from "#/lib/sale-search";
import { isSortKey, orderForSort } from "#/lib/sort";
import { useDebouncedCallback } from "#/lib/useDebouncedCallback";
import { type ProductFilters, useProducts } from "#/queries/products";
import { Pagination } from "../components/Pagination.js";
import { PillHero } from "../components/PillHero.js";
import { ProductCard, ProductGridSkeleton } from "../components/ProductCard.js";
import { BookCover } from "../components/primitives.js";

// The search endpoint filters on_sale server-side and returns only the sale
// subset, so one fetch covers the whole set (matches the endpoint's max).
const PER_PAGE = 24;
const SALE_LIMIT = 500;
const Q_DEBOUNCE_MS = 250;

function filtersFromSearch(search: SaleSearch): ProductFilters {
  return {
    q: search.q,
    onSale: true,
    limit: SALE_LIMIT,
    order: orderForSort(search.sort),
  };
}

export const Route = createFileRoute("/sale")({
  validateSearch: saleSearchSchema,
  head: () => ({ meta: [{ title: "Sale" }] }),
  component: SaleRoute,
});

function SaleRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const setSaleSearch: SetSaleSearch = (next) => {
    navigate({
      search: (prev: SaleSearch) => ({ ...prev, ...next }),
      replace: true,
      resetScroll: false,
    });
  };
  return <SalePage search={search} setSaleSearch={setSaleSearch} />;
}

interface SalePageProps {
  search: SaleSearch;
  setSaleSearch: SetSaleSearch;
}

function SalePage({ search, setSaleSearch }: SalePageProps) {
  const { data } = useProducts(filtersFromSearch(search));

  const [qDraft, setQDraft] = useState(search.q ?? "");
  const [syncedQ, setSyncedQ] = useState(search.q);
  if (search.q !== syncedQ) {
    setSyncedQ(search.q);
    setQDraft(search.q ?? "");
  }

  const { trigger: commitSearch } = useDebouncedCallback((value: string) => {
    const next = value.trim();
    const normalized = next.length > 0 ? next : undefined;
    if (normalized === search.q) return;
    setSaleSearch({ q: normalized, page: 1 });
  }, Q_DEBOUNCE_MS);

  const onSearchChange = (value: string) => {
    setQDraft(value);
    commitSearch(value);
  };

  const isLoading = data === undefined;
  const sorted = [...(data?.items ?? [])];
  if (search.sort === "price-asc")
    sorted.sort((a, b) => a.metadata.priceAmount - b.metadata.priceAmount);
  if (search.sort === "price-desc")
    sorted.sort((a, b) => b.metadata.priceAmount - a.metadata.priceAmount);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const visible = sorted.slice(
    (search.page - 1) * PER_PAGE,
    search.page * PER_PAGE,
  );

  return (
    <div data-screen-label="Sale" className="pb-24">
      <PillHero
        content={
          <>
            <h1 className="m-0 mb-4 whitespace-nowrap font-bold font-display text-[64px] text-lp-green-mid leading-none tracking-tight md:text-[80px] xl:text-[104px]">
              ON SALE
            </h1>
            <p className="m-0 mb-6 font-display text-lg text-lp-text">
              Book Sale: Up to 50% Off!
            </p>
            <SearchBar
              value={qDraft}
              onChange={onSearchChange}
              onSubmit={(q) => setSaleSearch({ q, page: 1 })}
              scope={{ onSale: true }}
              placeholder=""
              ariaLabel="Search sale items"
              className="relative w-full max-w-sm"
              inputClassName="h-12 w-full rounded-full border-[2.4px] border-lp-green bg-white pr-12 pl-5 font-display text-base text-lp-text outline-none focus:border-lp-green-mid"
              rightSlot={
                <svg
                  aria-hidden="true"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgb(171,215,158)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-4"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              }
            />
          </>
        }
        asset={<SaleHeroCovers />}
        assetClassName="md:-my-10 md:-ml-16"
      />
      <div className="mx-auto w-full max-w-figma px-6 md:px-10">
        <div className="mb-10 h-px bg-lp-green-mid/40" />
        <div className="mb-6 flex items-center justify-end">
          <div className="relative">
            <select
              value={search.sort}
              onChange={(e) => {
                if (isSortKey(e.target.value))
                  setSaleSearch({ sort: e.target.value, page: 1 });
              }}
              className="h-8 cursor-pointer appearance-none border-0 bg-transparent pr-6 pl-1 font-display text-lp-text text-sm outline-none"
            >
              <option value="default">Default Sorting</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name">Name (A–Z)</option>
            </select>
            <svg
              aria-hidden="true"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgb(122,170,107)"
              strokeWidth="2.5"
              strokeLinecap="round"
              className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-1"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>
        {isLoading ? (
          <ProductGridSkeleton count={PER_PAGE} />
        ) : visible.length === 0 ? (
          <div className="py-16 text-center font-display text-lp-text-lt">
            No sale items right now — check back soon!
          </div>
        ) : (
          <ul className="m-0 grid list-none grid-cols-2 md:grid-cols-3 gap-x-6 md:gap-x-12 gap-y-10 md:gap-y-14 p-0">
            {visible.map((p) => (
              <li key={p.id}>
                <ProductCard
                  product={p}
                  brandName={p.metadata.publisher ?? "Learning Plus"}
                />
              </li>
            ))}
          </ul>
        )}
        {!isLoading && (
          <Pagination
            page={search.page}
            total={totalPages}
            onPage={(page) => setSaleSearch({ page })}
          />
        )}
      </div>
    </div>
  );
}

function SaleHeroCovers() {
  const layers = [
    { src: ASSETS.bjuCovers[2], rotate: -22, dx: -180, dy: 70 },
    { src: ASSETS.bjuCovers[1], rotate: -6, dx: -90, dy: 25 },
    { src: ASSETS.bjuCovers[0], rotate: 12, dx: 0, dy: 0 },
  ];
  const bookW = 240;
  const bookH = 310;
  return (
    <div
      aria-hidden
      className="pointer-events-none relative"
      style={{ width: bookW, height: bookH }}
    >
      {layers.map((layer, i) =>
        layer.src ? (
          <div
            key={layer.src}
            style={{
              transform: `translate(${layer.dx}px, ${layer.dy}px) rotate(${layer.rotate}deg)`,
              zIndex: i,
              width: bookW,
              height: bookH,
            }}
            className="absolute top-0 left-0 flex items-center justify-center"
          >
            <BookCover
              src={layer.src}
              w={bookW}
              h={bookH}
              fit="contain"
              className="rounded-md shadow-card"
            />
          </div>
        ) : null,
      )}
    </div>
  );
}
